import { useForm } from '@mantine/form';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_CHECKLIST_COLOR,
  DEFAULT_CHECKLIST_ICON,
} from 'src/Components/Checklists/checklistVisuals.helper';

export interface CreateChecklistFormValues {
  title: string;
  items: { label: string; localId: string }[];
  icon: string;
  color: string;
  templateKey?: string;
}

export const useCreateChecklistForm = () => {
  const { t } = useTranslation();

  const form = useForm<CreateChecklistFormValues>({
    initialValues: {
      title: '',
      items: [{ localId: nanoid(), label: '' }],
      icon: DEFAULT_CHECKLIST_ICON,
      color: DEFAULT_CHECKLIST_COLOR,
      templateKey: undefined,
    },
    validate: {
      title: value => {
        if (!value || value.length < 3) {
          return t('checklist.form.invalidTitle', 'Titre invalide');
        }
      },
      items: value => {
        if (value.length < 1) {
          return t('checklist.form.needAtLeastOneItem', 'Au moins une option est requise');
        }

        if (value.some(item => !item || item.label.length < 3)) {
          return t(
            'checklist.form.itemTooShort',
            'Les options doivent au moins faire 3 caractères'
          );
        }
      },
    },
  });

  const addItem = () => {
    form.insertListItem('items', {
      label: '',
      localId: nanoid(),
    });
  };

  const removeItem = (index: number) => {
    form.removeListItem('items', index);
  };

  const reorderItems = (sourceIndex: number, destinationIndex: number) => {
    const items = [...form.values.items];
    const [removed] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, removed);

    form.setFieldValue('items', items);
  };

  return { form, addItem, removeItem, reorderItems };
};
